// Firebase keeps each origin's session; the private extension port synchronizes
// those sessions after Firebase has restored them from persistent storage.
import "./fs-shared";
import firebase from "firebase/compat/app";
import { onAuthStateChanged, signInWithCustomToken } from "firebase/auth";
import "firebase/compat/auth";
import { EXT_ID } from "@lipsurf/common/constants";
import { createAuthToken } from "@lipsurf/common/api";
import { AuthSessionBridge } from "@lipsurf/common/auth-session";
import { setAnalyticsAuthState } from "./telemetry";

export const auth = firebase.auth();
const sessions = new AuthSessionBridge({
  getUser: () => auth.currentUser,
  createToken: createAuthToken,
  signIn: (token) => signInWithCustomToken(auth, token),
  signOut: () => auth.signOut(),
  onError: () => console.warn("Could not synchronize the LipSurf session"),
});

onAuthStateChanged(auth, (user) => {
  setAnalyticsAuthState(!!user);
  sessions.authChanged();
});

let reconnect: ReturnType<typeof setTimeout> | undefined;
function retryConnection() {
  clearTimeout(reconnect);
  reconnect = setTimeout(() => {
    void connect();
  }, 5000);
}
function connect(): Promise<void> {
  if (typeof chrome === "undefined" || !chrome.runtime?.connect) {
    retryConnection();
    return Promise.resolve();
  }
  try {
    const port = chrome.runtime.connect(EXT_ID, { name: "lsComAuth" });
    const ready = sessions.connect(port, false);
    port.onDisconnect.addListener(() => {
      // Read lastError so an absent/reloaded extension does not log a Chrome error.
      void chrome.runtime.lastError;
      retryConnection();
    });
    return ready;
  } catch {
    retryConnection();
    return Promise.resolve();
  }
}

// Protected routes wait for both local persistence and the extension handoff.
export const authReady = Promise.all([sessions.localReady, connect()]).then(
  () => undefined
);

export function signOut() {
  return auth.signOut();
}
